import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PRODUCT_VISUAL_GROUPS, PRODUCT_VISUAL_OPTIONS, type ProductVisualKey } from "@ventas/shared";
import {
  useCategories,
  useCreateProduct,
  useDeactivateProduct,
  useProducts,
  useReactivateProduct,
  useUpdateProduct,
} from "./hooks";
import type { ProductCategory } from "./api";
import { useCreateCategory } from "../categories/hooks";
import { useSizeGuides } from "../size-guides/hooks";
import type { SizeGuide } from "../size-guides/api";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Sheet } from "../../components/Sheet";
import { PageHeader } from "../../components/PageHeader";
import { Toast } from "../../components/Toast";
import { ProductThumbnail } from "../../components/CatalogVisuals";
import { CheckIcon, PackageIcon, PencilIcon, PlusIcon, RulerIcon, SearchIcon, TagIcon, TrashIcon } from "../../components/icons";

type EditTarget =
  | { mode: "create" }
  | { mode: "edit"; productId: number; name: string; categoryId: number | null; sizeGuideIds: number[]; visualKey: ProductVisualKey };

export function AdminProductsView() {
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [feedback, setFeedback] = useState<{ kind: "success" | "error"; message: string } | null>(null);
  const list = useProducts(search || undefined, showInactive ? undefined : true);
  const categories = useCategories();
  const sizeGuides = useSizeGuides(true);
  const create = useCreateProduct();
  const update = useUpdateProduct();
  const deactivate = useDeactivateProduct();
  const reactivate = useReactivateProduct();

  return (
    <main className="page page--narrow">
      <PageHeader
        title="Productos"
        backHref="/more"
        actions={<Button variant="primary" onClick={() => setEditing({ mode: "create" })}><PlusIcon /> Nuevo</Button>}
      />

      <section className="page-section">
        <div className="section-intro">
          <div><p className="eyebrow">Catálogo</p><h1>Productos</h1><p>Administrá qué productos puede elegir el equipo de venta.</p></div>
          <div className="section-intro__actions">
            <Link className="secondary-link" to="/admin/categories"><TagIcon /> Categorías</Link>
            <Link className="secondary-link" to="/admin/size-guides"><RulerIcon /> Talles</Link>
          </div>
        </div>
        <div className="toolbar-row">
          <label className="search-control">
            <SearchIcon />
            <input type="search" aria-label="Buscar producto" placeholder="Buscar producto o categoría" value={search} onChange={(event) => setSearch(event.target.value)} />
          </label>
          <label className="switch-row"><input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} /> Ver inactivos</label>
        </div>

        {list.isLoading && <div className="skeleton-list"><span /><span /><span /></div>}
        {list.data?.length === 0 && (
          <div className="empty-state"><PackageIcon width={26} height={26} /><strong>{search ? "Sin resultados" : "Sin productos"}</strong><span>{search ? "Probá con otro nombre o categoría." : "Creá el primer producto para empezar a vender."}</span></div>
        )}
        <div className="entity-list">
          {list.data?.map((product) => (
            <article className={`entity-row product-row${product.active ? "" : " is-inactive"}`} key={product.id}>
              <ProductThumbnail productName={product.name} categoryName={product.category ?? undefined} categoryId={product.categoryId} visualKey={product.visualKey} />
              <div className="entity-row__body">
                <strong>{product.name}</strong>
                <span className="entity-row__meta">
                  <span className={`status-badge ${product.active ? "status-badge--success" : "status-badge--neutral"}`}>{product.active ? "Activo" : "Inactivo"}</span>
                  <small>{product.category ?? "Sin categoría"}{product.sizeGuides.length > 0 ? ` · ${product.sizeGuides.map((guide) => guide.name).join(" / ")}` : " · Sin talles"}</small>
                </span>
              </div>
              <div className="entity-row__actions">
              <button className="icon-action" type="button" aria-label={`Editar ${product.name}`} onClick={() => setEditing({ mode: "edit", productId: product.id, name: product.name, categoryId: product.categoryId, sizeGuideIds: product.sizeGuides.map((guide) => guide.id), visualKey: product.visualKey })}><PencilIcon /></button>
              <button
                className={`icon-action product-status-icon${product.active ? " is-deactivate" : ""}`}
                type="button"
                aria-label={product.active ? `Dar de baja ${product.name}` : `Reactivar ${product.name}`}
                title={product.active ? "Dar de baja" : "Reactivar producto"}
                disabled={deactivate.isPending || reactivate.isPending}
                onClick={async () => {
                  try {
                    if (product.active) await deactivate.mutateAsync(product.id);
                    else await reactivate.mutateAsync(product.id);
                    setFeedback({ kind: "success", message: product.active ? "Producto dado de baja" : "Producto reactivado" });
                  } catch {
                    setFeedback({ kind: "error", message: "No se pudo cambiar el estado del producto. Intentá nuevamente." });
                  }
                }}
              >{product.active ? <TrashIcon /> : <CheckIcon />}</button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <ProductEditSheet
        target={editing}
        categories={categories.data ?? []}
        sizeGuides={sizeGuides.data ?? []}
        onClose={() => setEditing(null)}
        onSave={async (payload) => {
          if (!editing) return;
          if (editing.mode === "create") await create.mutateAsync(payload);
          else await update.mutateAsync({ id: editing.productId, body: payload });
          setEditing(null);
          setFeedback({ kind: "success", message: editing.mode === "create" ? "Producto creado" : "Producto actualizado" });
        }}
        saving={create.isPending || update.isPending}
      />
      {feedback && <Toast kind={feedback.kind} message={feedback.message} icon={feedback.kind === "success" ? <CheckIcon /> : undefined} autoCloseMs={2400} onDismiss={() => setFeedback(null)} />}
    </main>
  );
}

function ProductEditSheet({ target, categories, sizeGuides, onClose, onSave, saving }: {
  target: EditTarget | null;
  categories: ProductCategory[];
  sizeGuides: SizeGuide[];
  onClose: () => void;
  onSave: (payload: { name: string; categoryId: number | null; sizeGuideIds: number[]; visualKey: ProductVisualKey }) => Promise<void>;
  saving: boolean;
}) {
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [sizeGuideIds, setSizeGuideIds] = useState<number[]>([]);
  const [visualKey, setVisualKey] = useState<ProductVisualKey>("package");
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createCategory = useCreateCategory();

  useEffect(() => {
    if (!target) return;
    setName(target.mode === "edit" ? target.name : "");
    setCategoryId(target.mode === "edit" && target.categoryId ? String(target.categoryId) : "");
    setSizeGuideIds(target.mode === "edit" ? target.sizeGuideIds : []);
    setVisualKey(target.mode === "edit" ? target.visualKey : "package");
    setNewCategory("");
    setCreatingCategory(false);
    setError(null);
  }, [target]);

  if (!target) return null;

  const selectedCategoryName = categories.find((category) => String(category.id) === categoryId)?.name;
  const selectedVisual = PRODUCT_VISUAL_OPTIONS.find((option) => option.key === visualKey);

  return (
    <Sheet open onClose={onClose} title={target.mode === "create" ? "Nuevo producto" : "Editar producto"}>
      <form className="form-stack" onSubmit={async (event) => {
        event.preventDefault();
        if (!name.trim()) return setError("Ingresá un nombre.");
        try {
          let selectedId = categoryId ? Number(categoryId) : null;
          if (creatingCategory) {
            if (newCategory.trim().length < 2) return setError("Ingresá un nombre de categoría válido.");
            const created = await createCategory.mutateAsync({ name: newCategory.trim() });
            selectedId = created.id;
          }
          await onSave({ name: name.trim(), categoryId: selectedId, sizeGuideIds, visualKey });
        } catch {
          setError("No se pudo guardar. Revisá que la categoría no esté repetida.");
        }
      }}>
        <Input label="Nombre" value={name} onChange={(event) => { setName(event.target.value); setError(null); }} autoFocus />
        <label className="field-control">
          <span>Categoría</span>
          <select value={categoryId} onChange={(event) => { setCategoryId(event.target.value); setCreatingCategory(false); setError(null); }} disabled={creatingCategory}>
            <option value="">Sin categoría</option>
            {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
          </select>
        </label>
        <button className="inline-create" type="button" onClick={() => setCreatingCategory((value) => !value)}><PlusIcon /> {creatingCategory ? "Elegir categoría existente" : "Crear categoría nueva"}</button>
        {creatingCategory && <Input label="Nombre de la categoría nueva" value={newCategory} onChange={(event) => { setNewCategory(event.target.value); setError(null); }} />}
        <div className="product-visual-field">
          <label className="field-control">
            <span>Ilustración</span>
            <select value={visualKey} onChange={(event) => setVisualKey(event.target.value as ProductVisualKey)}>
              {PRODUCT_VISUAL_GROUPS.map((group) => (
                <optgroup label={group} key={group}>
                  {PRODUCT_VISUAL_OPTIONS.filter((option) => option.group === group).map((option) => (
                    <option value={option.key} key={option.key}>{option.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <div className="product-visual-preview product-visual-preview--compact" aria-label={`Vista previa: ${selectedVisual?.label ?? "Genérica"}`}>
            <ProductThumbnail
              productName={name || "Producto"}
              categoryName={selectedCategoryName}
              categoryId={categoryId ? Number(categoryId) : null}
              visualKey={visualKey}
            />
          </div>
        </div>
        <fieldset className="choice-fieldset">
          <legend>Guías de talles <span>(opcional)</span></legend>
          {sizeGuides.length === 0 ? (
            <p>No hay guías activas. <Link to="/admin/size-guides" onClick={onClose}>Creá la primera</Link>.</p>
          ) : sizeGuides.map((guide) => (
            <label className="choice-row" key={guide.id}>
              <input
                type="checkbox"
                checked={sizeGuideIds.includes(guide.id)}
                onChange={(event) => setSizeGuideIds((current) => event.target.checked ? [...current, guide.id] : current.filter((id) => id !== guide.id))}
              />
              <span><strong>{guide.name}</strong><small>{guide.values.join(" · ")}</small></span>
            </label>
          ))}
        </fieldset>
        {error && <span className="form-error" role="alert">{error}</span>}
        <Button type="submit" variant="primary" fullWidth size="lg" disabled={saving || createCategory.isPending}>{saving || createCategory.isPending ? "Guardando…" : "Guardar producto"}</Button>
      </form>
    </Sheet>
  );
}
